import poppinsExtraLightLatinWoff2 from '@fontsource/poppins/files/poppins-latin-200-normal.woff2?url'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { FAQS } from '~/components/marketing/faq-section'
import { MarketingHomePage } from '~/components/marketing/marketing-home-page'
import { hasStoredAuthTokenHint } from '~/lib/auth-hint'
import { isMarketingBoot } from '~/lib/router-auth'
import { alternateLinks, ogImageFor, ogImageMeta, ogLocaleMeta, SITE_URL } from '~/lib/site-meta'

// SERP title — keyword-first so Google distinguishes this Planno from the
// solar (planno.io) and kanban (planno.co) entities that dominate the bare
// "Planno" SERP. Keep under ~60 chars to avoid truncation.
const PAGE_TITLE = 'Planno — gestão para escritórios de arquitetura e engenharia'
// Social title — poetic, used in OG/Twitter cards where length matters less.
const SOCIAL_TITLE = 'Planno — o escritório inteiro em um só lugar'
const HOME_DESCRIPTION =
  'A ferramenta profissional que reúne projetos, tarefas, sprints, visitas técnicas e clientes do escritório de arquitetura e engenharia em um só lugar.'
const KEYWORDS =
  'software para escritório de arquitetura, ferramenta profissional para arquitetos, sistema para arquitetos, gestão de projetos de arquitetura, gestão de obra, ERP para arquitetura, gestão de escritório de engenharia, visita técnica, checklist de obra, sprints, kanban para arquitetos'

export const Route = createFileRoute('/')({
  // Optimistic local check instead of `await ensureAuth()`: a stored Convex
  // Auth JWT means a signed-in (or recently signed-in) user, so send them
  // straight to the app without blocking on the token-verification round
  // trip. `/home` sits behind the `_authenticated` gate, which does the real
  // check and bounces a stale session to `/sign-in`. Anonymous visitors
  // render the marketing page immediately, with no auth wait at all.
  beforeLoad: () => {
    if (!hasStoredAuthTokenHint()) {
      return
    }
    // Under the Convex-free marketing router, `/home` can't run in-router —
    // hard-navigate so the fresh document boots the full app router, and
    // freeze this transition while the browser unloads the page. Under the
    // app router (an in-app navigation back to `/`), redirect in-place.
    if (isMarketingBoot()) {
      window.location.replace('/home')
      return new Promise<never>(() => {
        // Intentionally never resolves — the browser is unloading the page.
      })
    }
    throw redirect({ to: '/home' })
  },
  component: MarketingHomePage,
  head: () => ({
    links: [
      { href: `${SITE_URL}/`, rel: 'canonical' },
      ...alternateLinks('/'),
      /* The hero's light half renders in Poppins ExtraLight above the fold;
         preload it here (not in __root) so app routes don't pay for a
         weight only this page uses. */
      {
        as: 'font',
        crossOrigin: 'anonymous',
        href: poppinsExtraLightLatinWoff2,
        rel: 'preload',
        type: 'font/woff2',
      },
    ],
    meta: [
      { title: PAGE_TITLE },
      { content: HOME_DESCRIPTION, name: 'description' },
      { content: KEYWORDS, name: 'keywords' },
      // Open Graph
      { content: 'website', property: 'og:type' },
      { content: 'Planno', property: 'og:site_name' },
      { content: SOCIAL_TITLE, property: 'og:title' },
      { content: HOME_DESCRIPTION, property: 'og:description' },
      { content: `${SITE_URL}/`, property: 'og:url' },
      ...ogImageMeta(),
      ...ogLocaleMeta('pt-BR'),
      // Twitter / X
      { content: 'summary_large_image', name: 'twitter:card' },
      { content: SOCIAL_TITLE, name: 'twitter:title' },
      { content: HOME_DESCRIPTION, name: 'twitter:description' },
    ],
    scripts: [
      {
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          alternateName: 'Planno App',
          applicationCategory: 'BusinessApplication',
          applicationSubCategory: 'ProjectManagementApplication',
          audience: {
            '@type': 'BusinessAudience',
            audienceType: 'Escritórios de arquitetura e engenharia',
          },
          description: HOME_DESCRIPTION,
          featureList: [
            'Gestão de projetos de arquitetura e engenharia',
            'Tarefas em sprints semanais e kanban',
            'Visitas técnicas com checklists e fotos',
            'Arquivos do projeto com versionamento',
            'Documentos colaborativos e memorial descritivo',
            'Conversas integradas ao projeto',
            'Gestão de equipe e contatos (clientes, fornecedores, consultores)',
          ],
          image: ogImageFor('pt-BR'),
          inLanguage: 'pt-BR',
          keywords: KEYWORDS,
          name: 'Planno',
          offers: [
            {
              '@type': 'Offer',
              availability: 'https://schema.org/InStock',
              name: 'Pessoal',
              price: '0',
              priceCurrency: 'BRL',
            },
            {
              '@type': 'Offer',
              availability: 'https://schema.org/InStock',
              category: 'Subscription',
              name: 'Profissional',
              priceCurrency: 'BRL',
              priceSpecification: {
                '@type': 'UnitPriceSpecification',
                price: '49',
                priceCurrency: 'BRL',
                referenceQuantity: {
                  '@type': 'QuantitativeValue',
                  unitCode: 'C62',
                  unitText: 'user',
                  value: '1',
                },
                unitCode: 'MON',
              },
            },
          ],
          operatingSystem: 'Web',
          provider: {
            '@type': 'Organization',
            name: 'Planno',
            url: SITE_URL,
          },
          url: `${SITE_URL}/`,
        }),
        type: 'application/ld+json',
      },
      {
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: FAQS.map((faq) => ({
            '@type': 'Question',
            acceptedAnswer: {
              '@type': 'Answer',
              text: faq.a,
            },
            name: faq.q,
          })),
        }),
        type: 'application/ld+json',
      },
      // Top-level Organization for entity disambiguation. Google uses this
      // (especially `sameAs`) to tell our Planno apart from Planno.io (solar),
      // Planno.co (kanban), and the dozens of Brazilian businesses named Planno.
      // Fill in the `sameAs` URLs once each social handle is live.
      {
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@id': `${SITE_URL}/#organization`,
          '@type': 'Organization',
          areaServed: { '@type': 'Country', name: 'Brasil' },
          description: HOME_DESCRIPTION,
          knowsLanguage: ['pt-BR', 'en'],
          legalName: 'Planno',
          logo: `${SITE_URL}/planno-mark-120.png`,
          name: 'Planno',
          // Add live profile URLs as they go up — these are what tell Google
          // this is a distinct entity from other "Planno" organizations.
          sameAs: [
            // 'https://www.linkedin.com/company/planno-app',
            // 'https://www.instagram.com/planno.app',
            // 'https://x.com/plannoapp',
          ],
          slogan: SOCIAL_TITLE,
          url: SITE_URL,
        }),
        type: 'application/ld+json',
      },
      {
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@id': `${SITE_URL}/#website`,
          '@type': 'WebSite',
          alternateName: 'Planno — escritório de arquitetura e engenharia',
          inLanguage: 'pt-BR',
          name: 'Planno',
          publisher: { '@id': `${SITE_URL}/#organization` },
          url: SITE_URL,
        }),
        type: 'application/ld+json',
      },
    ],
  }),
})
